import type { TemplateDocumentData } from "@/types/models";

const REVIEWED_STATUSES = new Set(["Reviewed", "Approved"]);

export interface GridReviewState {
  isReviewed: boolean;
  reviewed: number;
  total: number;
}

export type GridReviewMap = Map<string, GridReviewState>;

/** Fallback for a grid absent from the map — a grid with no rows is never available. */
export const UNREVIEWED_GRID: GridReviewState = Object.freeze({
  isReviewed: false,
  reviewed: 0,
  total: 0,
});

interface ReviewableRow {
  data: string | null;
}

interface ExtractionPoint {
  id: string;
  name: string;
  parent_id?: string | null;
}

function parseFields(data: string | null): Record<string, unknown> | null {
  if (!data) return null;
  try {
    const parsed = JSON.parse(data);
    if (!parsed || typeof parsed !== "object") return null;
    const { fields } = parsed as { fields?: unknown };
    return fields && typeof fields === "object" ? (fields as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function isCellReviewed(cell: unknown): boolean {
  if (!cell || typeof cell !== "object") return false;
  const { status } = cell as { status?: unknown };
  return typeof status === "string" && REVIEWED_STATUSES.has(status);
}

/**
 * Cell status inside the `data` blob is the ground truth for review state —
 * the row-level `status` column is overloaded with the extraction lifecycle.
 *
 * A row must carry a reviewed cell for every column the grid expects: cells are
 * keyed by column name, so a renamed or newly added column leaves older rows
 * without one. Absent, malformed and empty data all count as not reviewed, as
 * does a row with no known column list to check against.
 */
export function isRowReviewed(data: string | null, expectedColumns: string[]): boolean {
  if (expectedColumns.length === 0) return false;
  const fields = parseFields(data);
  if (!fields) return false;
  if (!expectedColumns.every((name) => isCellReviewed(fields[name]))) return false;
  return Object.values(fields).every(isCellReviewed);
}

function summariseRows(rows: ReviewableRow[], expectedColumns: string[]): GridReviewState {
  let reviewed = 0;
  for (const row of rows) {
    if (isRowReviewed(row.data, expectedColumns)) reviewed++;
  }
  return { isReviewed: rows.length > 0 && reviewed === rows.length, reviewed, total: rows.length };
}

export function isGridReviewed(rows: ReviewableRow[], expectedColumns: string[]): boolean {
  return summariseRows(rows, expectedColumns).isReviewed;
}

export function gridReviewProgress(
  rows: ReviewableRow[],
  expectedColumns: string[]
): { reviewed: number; total: number } {
  const { reviewed, total } = summariseRows(rows, expectedColumns);
  return { reviewed, total };
}

/** Column names each template expects, keyed by template id. */
export function buildColumnNameMap(extractionPoints: ExtractionPoint[]): Map<string, string[]> {
  const namesByTemplate = new Map<string, string[]>();
  for (const point of extractionPoints) {
    if (!point.parent_id) continue;
    const existing = namesByTemplate.get(point.parent_id);
    if (existing) existing.push(point.name);
    else namesByTemplate.set(point.parent_id, [point.name]);
  }
  return namesByTemplate;
}

/** Groups already-flattened active rows by template so call sites share one derivation. */
export function buildGridReviewMap(
  documents: TemplateDocumentData[],
  extractionPoints: ExtractionPoint[]
): GridReviewMap {
  const columnsByTemplate = buildColumnNameMap(extractionPoints);
  const rowsByTemplate = new Map<string, TemplateDocumentData[]>();
  for (const doc of documents) {
    const existing = rowsByTemplate.get(doc.projectTemplateId);
    if (existing) existing.push(doc);
    else rowsByTemplate.set(doc.projectTemplateId, [doc]);
  }

  const reviewByTemplate: GridReviewMap = new Map();
  for (const [templateId, rows] of rowsByTemplate) {
    reviewByTemplate.set(templateId, summariseRows(rows, columnsByTemplate.get(templateId) ?? []));
  }
  return reviewByTemplate;
}

/** Drops saved data point ids whose grid is not available for use. */
export function filterAvailableExtractionPointIds(
  selectedIds: string[],
  extractionPoints: ExtractionPoint[],
  gridReview: GridReviewMap
): string[] {
  if (selectedIds.length === 0) return [];
  const templateIdByPoint = new Map(extractionPoints.map((point) => [point.id, point.parent_id]));
  return selectedIds.filter((id) => {
    const templateId = templateIdByPoint.get(id) ?? id;
    return gridReview.get(templateId)?.isReviewed === true;
  });
}
